import type { PopupLayout } from 'vars/index'
import type { PopupRowGap } from './Popup.h'

import { styled } from '@linaria/react'
import { COLOR, CORNERS, FIXED_INDENTS, MEDIA } from 'vars/index'

export const PopupOverlaySC = styled.div<{
  overlayColor: COLOR
  overlayGradient?: COLOR
}>`
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  z-index: 8;
  background-color: ${({ overlayColor }) => `var(${overlayColor})`};
  background-image: ${({ overlayGradient }) =>
    overlayGradient ? `var(${overlayGradient})` : 'unset'};
  background-repeat: no-repeat;
  width: 100vw;
`

export const PopupOverlayCloseSC = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
`

export const PopupComponentSC = styled.div<{
  backgroundColor: COLOR
  backgroundGradient?: COLOR
  paddingInline: PopupLayout
  width?: string
  isShoppingCartPopup?: boolean
  isKeyboardLikelyOpen?: boolean
  isContentDetailsPopup?: boolean
}>`
  --scrollBarWidth: ${({ isContentDetailsPopup }) =>
    isContentDetailsPopup ? '3px' : '8px'};
  --scrollBarThumbColor: ${({ isContentDetailsPopup }) =>
    isContentDetailsPopup
      ? `var(${COLOR.SURFACE_DARK_5})`
      : `var(${COLOR.SURFACE_OPACITY_LIGHT_10})`};
  --scrollBarThumbCorners: ${({ isContentDetailsPopup }) =>
    isContentDetailsPopup
      ? `var(${CORNERS.CORNERS_5})`
      : `var(${CORNERS.CORNERS_10})`};
  --popupGradientHeight: ${({ isContentDetailsPopup }) =>
    isContentDetailsPopup ? `var(${FIXED_INDENTS.INDENT_140})` : '4.8vh'};
  --popupWidth: 100%;

  height: 100%;
  width: 100%;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  background-color: ${({ backgroundColor }) => `var(${backgroundColor})`};
  background-image: ${({ backgroundGradient }) => `var(${backgroundGradient})`};
  border-radius: var(${CORNERS.CORNERS_10});
  padding-top: var(${FIXED_INDENTS.INDENT_140});

  --scrollbarOffset: 8px;
  --horizontalPadding: ${({ paddingInline, isContentDetailsPopup }) =>
    isContentDetailsPopup
      ? `var(${FIXED_INDENTS.INDENT_160})`
      : `var(${paddingInline})`};
  --paddingRight: calc(var(--horizontalPadding) - var(--scrollbarOffset));
  --paddingLeft: var(--horizontalPadding);
  --heightForMobile: 40%;

  ${MEDIA.MIN_600} {
    --popupWidth: ${({ width }) => width ?? '528px'};
    width: var(--popupWidth);
    max-height: ${({ isContentDetailsPopup }) =>
      isContentDetailsPopup
        ? `calc(100dvh - var(${FIXED_INDENTS.INDENT_200}))`
        : '80vh'};
    height: ${({ isContentDetailsPopup }) =>
      isContentDetailsPopup ? '740px' : 'auto'};
  }

  ${MEDIA.MAX_599} {
    --horizontalPadding: ${({ paddingInline, isContentDetailsPopup }) =>
      isContentDetailsPopup
        ? `var(${FIXED_INDENTS.INDENT_60})`
        : `var(${paddingInline})`};

    justify-content: ${({ isShoppingCartPopup, isKeyboardLikelyOpen }) => {
      if (isShoppingCartPopup || isKeyboardLikelyOpen) return 'center'
      return '72px'
    }};
  }
`

export const PopupTopBlockSC = styled.div<{ isShoppingCartPopup?: boolean }>`
  position: absolute;
  top: 8px;
  right: 8px;
`

export const PopupHeaderSC = styled.div<{
  paddingsHeaderBottom: PopupLayout
}>`
  padding: ${({ paddingsHeaderBottom }) => `
    0px var(--paddingLeft) var(${paddingsHeaderBottom})`};
`

/**
 * Header variant for popups whose header scrolls with the body. The scrollable
 * block already applies the horizontal padding, so only the gap below the
 * header is left here.
 */
export const PopupScrolledHeaderSC = styled.div<{
  paddingsHeaderBottom: PopupLayout
}>`
  flex: 0 0 auto;
  padding-bottom: ${({ paddingsHeaderBottom }) =>
    `var(${paddingsHeaderBottom})`};
`

export const PopupGradientOverlay = styled.div`
  display: flex;
  flex: 1;
  min-height: 1px;
  position: relative;

  &:before,
  &:after {
    content: '';
    position: absolute;
    width: calc(100% - var(--scrollbarOffset));
    height: var(--popupGradientHeight);
    pointer-events: none;
    z-index: 1;
    opacity: 0;
  }

  &:before {
    top: -1px;
    background: var(${COLOR.MAIN_GRADIENT_180});
  }

  &:after {
    bottom: -1px;
    background: var(${COLOR.MAIN_GRADIENT_0});
  }

  /* Content details panel is SURFACE_DARK_4; MAIN_GRADIENT is #141515 and
     reads as a dark band on the first and last visible lines. */
  &[data-content-details='true']:before {
    background: linear-gradient(
      180deg,
      var(${COLOR.SURFACE_DARK_4}) 0%,
      color-mix(in srgb, var(${COLOR.SURFACE_DARK_4}) 0%, transparent) 100%
    );
  }

  &[data-content-details='true']:after {
    background: linear-gradient(
      0deg,
      var(${COLOR.SURFACE_DARK_4}) 0%,
      color-mix(in srgb, var(${COLOR.SURFACE_DARK_4}) 0%, transparent) 100%
    );
  }

  &[data-top-fade='true']:before {
    opacity: 1;
  }

  &[data-bottom-fade='true']:after {
    opacity: 1;
  }
`

export const PopupScrollableBlockWithGradient = styled.div<{}>`
  overflow: auto;
  overscroll-behavior-y: none;
  display: flex;
  flex-direction: column;
  padding: 0 var(--paddingRight) 0 var(--paddingLeft);
  width: calc(100% - var(--scrollbarOffset));

  &::-webkit-scrollbar {
    width: var(--scrollBarWidth);
  }

  &::-webkit-scrollbar-thumb {
    background: var(--scrollBarThumbColor);
    border-radius: var(--scrollBarThumbCorners);
    cursor: pointer;
  }
`

export const PopupScrollableBlockDefault = styled.div<{
  rowGapDescription: PopupRowGap
}>`
  overflow: auto;

  width: calc(100% - var(--scrollbarOffset));
  padding: 0 var(--paddingRight) 0 var(--paddingLeft);

  &::-webkit-scrollbar {
    width: var(--scrollBarWidth);
  }

  &::-webkit-scrollbar-thumb {
    background: var(--scrollBarThumbColor);
    border-radius: var(--scrollBarThumbCorners);
    cursor: pointer;
  }
`

export const PopupFooterWrapperSC = styled.div<{ paddingTop: FIXED_INDENTS }>`
  padding: ${({ paddingTop }) => `var(${paddingTop}) var(--paddingLeft) 0`};
`

export const PopupBottomBlockSC = styled.div`
  flex: 0 0 56px;
`

export const PINLockPlayerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-self: start;
  max-width: 28.75rem;
  position: absolute;
  transform: translate(-50%, -50%);
  left: 50%;
  top: 50vh;

  row-gap: var(${FIXED_INDENTS.INDENT_60});
`

export const PINLockPopupWrapper = styled.div`
  ${MEDIA.MAX_599} {
    ${PopupOverlaySC} {
      height: 100dvh;
    }
  }
`

export const ProfilesPopupWrapper = styled.div`
  ${PopupComponentSC} {
    padding-top: 0;

    ${MEDIA.MIN_600} {
      --popupWidth: 520px;
    }

    ${MEDIA.MIN_1240_OR_LANDSCAPE} {
      --popupWidth: 696px;
      padding: var(${FIXED_INDENTS.INDENT_120});
      max-height: 85dvh;
    }

    ${MEDIA.MIN_1240} {
      --popupWidth: 796px;
      padding: var(${FIXED_INDENTS.INDENT_120}) var(${FIXED_INDENTS.INDENT_120})
        var(${FIXED_INDENTS.INDENT_120}) var(${FIXED_INDENTS.INDENT_220});
    }
  }

  ${PopupHeaderSC} {
    padding: 0;
    height: 100%;

    ${MEDIA.MIN_600} {
      height: initial;
    }
  }

  ${PopupTopBlockSC} {
    display: none;
  }

  ${PopupBottomBlockSC} {
    display: none;
  }
`
